import React from 'react';
import Modal from '../ui/Modal';
import { AlertTriangle, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface DuplicateTicket {
  ticket_id: number;
  assunto: string;
  departamento_id: number;
  departamento_nome: string;
  status_id: number;
  status_nome: string;
  usuario_nome: string;
  created_at: string;
}

interface DuplicateTicketModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  duplicates: DuplicateTicket[];
}

export default function DuplicateTicketModal({
  isOpen,
  onClose,
  onConfirm,
  duplicates,
}: DuplicateTicketModalProps) {
  const navigate = useNavigate();

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusColor = (statusId: number) => {
    if (statusId === 2) return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300';
    if (statusId === 1) return 'bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300';
    return 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-300';
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-amber-100 dark:bg-amber-900/50 rounded-full flex items-center justify-center">
            <AlertTriangle className="w-6 h-6 text-amber-600 dark:text-amber-400" />
          </div>
          <div>
            <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Tickets Duplicados Encontrados
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {duplicates.length} ticket{duplicates.length > 1 ? 's' : ''} similar{duplicates.length > 1 ? 'es' : ''} encontrado{duplicates.length > 1 ? 's' : ''}
            </p>
          </div>
        </div>
      }
      maxWidth="2xl"
      closeOnBackdropClick={false}
    >
      <div className="p-6">
        <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-lg">
          <p className="text-sm text-amber-800 dark:text-amber-300">
            <strong>Atenção:</strong> Encontramos {duplicates.length} ticket{duplicates.length > 1 ? 's' : ''} aberto{duplicates.length > 1 ? 's' : ''} com as mesmas características (mesmo departamento, assunto, descrição e criado hoje). 
            Deseja continuar mesmo assim?
          </p>
        </div>

        <div className="space-y-3 mb-6 max-h-96 overflow-y-auto">
          {duplicates.map((duplicate) => (
            <div
              key={duplicate.ticket_id}
              className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-gray-300 dark:hover:border-gray-600 transition-colors bg-white dark:bg-gray-800"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-semibold text-gray-900 dark:text-gray-100">
                      Ticket #{duplicate.ticket_id}
                    </span>
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(duplicate.status_id)}`}
                    >
                      {duplicate.status_nome}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700 dark:text-gray-300 mb-1">
                    <strong>Assunto:</strong> {duplicate.assunto}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                    <strong>Departamento:</strong> {duplicate.departamento_nome}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-500">
                    Criado em {formatDate(duplicate.created_at)} por {duplicate.usuario_nome}
                  </p>
                </div>
                <button
                  onClick={() => {
                    navigate(`/tickets/${duplicate.ticket_id}`);
                    onClose();
                  }}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                  title="Abrir ticket"
                >
                  <ExternalLink className="w-4 h-4" />
                  Abrir
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors font-medium"
          >
            Cancelar
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            Continuar Mesmo Assim
          </button>
        </div>
      </div>
    </Modal>
  );
}

